import { useEffect, useState, useCallback, useRef } from "react";
import ToggleSwitch from "../../../common/Switch/ToggleSwitch";
import {
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Modal,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useLoading } from "../../../hooks/useLoader";
import ApiServices from "../../../services/ApiServices";
import {
    ADDIN_CONFIG_ALL_SUBS,
    ADDIN_CONFIG_ALL_SUBSUPDATE,
    SUBSCRIBER_LIST_ID,
} from "../../../constants/api-constants";
import { MandatoryField } from "../../../common/MandatoryFieldsIcon/MandatoryField";
import MultiSelectCheckboxSearchableDropdown from "../../../common/Modal/MultiSelectCheckboxSearchableDropdown/MultiSelectCheckboxSearchableDropdown";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import "../../../common/Modal/Modal.css";

const addSubscriberModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(1120px, calc(100vw - 64px))",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 0,
    borderRadius: 0,
};

const AddInsManageSubscriber = ({ enterpriseId, addInId, onSubscribersChange, assignmentMode = 2, refreshKey = 0 }) => {
    const [subscribers, setSubscribers] = useState([]);
    const [selectedSubscribers, setSelectedSubscribers] = useState(new Set());
    const [searchText, setSearchText] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [isAddSubscriberModalOpen, setIsAddSubscriberModalOpen] = useState(false);
    const [modalSubscriberOptions, setModalSubscriberOptions] = useState([]);
    const [modalSelectedSubscribers, setModalSelectedSubscribers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortColumn, setSortColumn] = useState("");
    const [sortType, setSortType] = useState("");
    const { showLoader } = useLoading();
    const searchTimeoutRef = useRef(null);

    // Use a ref so onSubscribersChange never causes fetchSubscribers to be recreated
    const onSubscribersChangeRef = useRef(onSubscribersChange);
    useEffect(() => {
        onSubscribersChangeRef.current = onSubscribersChange;
    });

    /**
     * Fetch subscribers from the API using ADDIN_CONFIG_ALL_SUBS
     */
    const fetchSubscribers = useCallback(async () => {
        if (!enterpriseId || !addInId) return;

        setLoading(true);
        showLoader(true);

        try {
            const payload = {
                isShowChildUserData: true,
                searchText,
                SearchText: searchText,
                selectedIds: [],
                pageSize,
                PageSize: pageSize,
                pageNumber,
                PageNumber: pageNumber,
                ignorePaging: false,
                sortColumn,
                sortType,
                filter: {
                    enterpriseGroupId: enterpriseId,
                    addInId,
                    searchText,
                    SearchText: searchText,
                },
                isSearchInAllQuery: true,
            };

            const response = await ApiServices.httpPost(
                ADDIN_CONFIG_ALL_SUBS(enterpriseId, addInId),
                payload,
            );

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(
                    response?.responseStatus?.error || "Failed to load subscribers.",
                );
            }

            const result = response?.result || {};
            const subscriberList = Array.isArray(result)
                ? result
                : result?.associatedSubscribers ||
                result?.subscribers ||
                result?.records ||
                result?.items ||
                [];
            const safeSubscribers = Array.isArray(subscriberList) ? subscriberList : [];

            setSubscribers(safeSubscribers);

            const initiallySelectedIds = safeSubscribers
                .filter(
                    (subscriber) =>
                        subscriber?.hasAccess === true ||
                        subscriber?.isAssociated === true ||
                        subscriber?.isSelected === true,
                )
                .map((subscriber) =>
                    Number(
                        subscriber?.subscriberId ||
                        subscriber?.id ||
                        subscriber?.distributorUserId ||
                        0,
                    ),
                )
                .filter((id) => id > 0);
            setSelectedSubscribers(new Set(initiallySelectedIds));
            if (onSubscribersChangeRef.current) {
                onSubscribersChangeRef.current(initiallySelectedIds);
            }

            // Calculate totals/pages from several known API shapes
            const rawTotalCount =
                result?.totalSubscribers ??
                result?.totalCount ??
                result?.recordCount ??
                result?.recordsCount ??
                result?.count ??
                response?.totalCount ??
                response?.recordCount ??
                response?.resultCount;
            const parsedTotalCount = Number(rawTotalCount);
            const resolvedTotalCount =
                Number.isFinite(parsedTotalCount) && parsedTotalCount >= 0
                    ? parsedTotalCount
                    : safeSubscribers.length;

            const rawTotalPages = result?.totalPages ?? response?.totalPages;
            const parsedTotalPages = Number(rawTotalPages);
            const resolvedTotalPages =
                Number.isFinite(parsedTotalPages) && parsedTotalPages > 0
                    ? parsedTotalPages
                    : Math.max(1, Math.ceil(resolvedTotalCount / pageSize));

            setTotalCount(resolvedTotalCount);
            setTotalPages(resolvedTotalPages);
        } catch (error) {
            toast.error(error?.message || "Failed to load subscribers.");
            setSubscribers([]);
            setTotalCount(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
            showLoader(false);
        }
    }, [enterpriseId, addInId, searchText, pageNumber, pageSize, sortColumn, sortType, showLoader]);

    /**
     * Load subscribers on component mount or when dependencies change
     */
    useEffect(() => {
        fetchSubscribers();
    }, [fetchSubscribers, refreshKey]);

    /**
     * Handle search text input with debouncing
     */
    const handleSearchChange = (event) => {
        const searchValue = event.target.value;

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout with 1 second delay (matching other screens)
        searchTimeoutRef.current = setTimeout(() => {
            setSearchText(searchValue);
            setPageNumber(1); // Reset to first page on search
        }, 1000);
    };

    /**
     * Handle subscriber toggle and call PUT API to update access
     */
    const handleToggleSubscriber = async (subscriberId) => {
        const isCurrentlySelected = selectedSubscribers.has(subscriberId);
        const newHasAccess = !isCurrentlySelected;

        // Update local state optimistically
        const newSelected = new Set(selectedSubscribers);
        if (newHasAccess) {
            newSelected.add(subscriberId);
        } else {
            newSelected.delete(subscriberId);
        }
        setSelectedSubscribers(newSelected);

        // Call PUT API to persist the change
        try {
            showLoader(true);
            const payload = {
                assignmentMode,
                subscriberAccess: [
                    {
                        subscriberId,
                        hasAccess: newHasAccess,
                    },
                ],
            };

            const response = await ApiServices.httpPut(
                ADDIN_CONFIG_ALL_SUBSUPDATE(enterpriseId, addInId),
                payload,
            );

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(
                    response?.responseStatus?.error || "Failed to update subscriber access.",
                );
            }

            toast.success(
                newHasAccess
                    ? "Subscriber access granted."
                    : "Subscriber access denied."
            );

            // Notify parent component of changes
            if (onSubscribersChangeRef.current) {
                onSubscribersChangeRef.current(Array.from(newSelected));
            }

            await fetchSubscribers();
        } catch (error) {
            // Revert optimistic update on error
            const revertSelected = new Set(selectedSubscribers);
            if (isCurrentlySelected) {
                revertSelected.add(subscriberId);
            } else {
                revertSelected.delete(subscriberId);
            }
            setSelectedSubscribers(revertSelected);

            toast.error(error?.message || "Failed to update subscriber access.");
        } finally {
            showLoader(false);
        }
    };

    /**
     * Handle page change
     */
    const handlePageChange = (_event, newPage) => {
        const boundedPage = Math.min(Math.max(1, newPage), totalPages || 1);
        if (boundedPage !== pageNumber) {
            setPageNumber(boundedPage);
        }
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(1);
    };

    const handleSortChange = (column) => {
        if (sortColumn === column) {
            setSortType((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortType("asc");
        }
        setPageNumber(1);
    };

    const getSortImage = (column) => {
        if (sortColumn !== column) {
            return "/images/unsorted.png";
        }

        return sortType === "asc"
            ? "/images/Ascending@2x-1.png"
            : "/images/Descending@2x-1.png";
    };

    // Cleanup search timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const openAddSubscriberModal = async () => {
        if (!enterpriseId || !addInId) return;

        try {
            showLoader(true);
            const response = await ApiServices.httpGet(
                `${SUBSCRIBER_LIST_ID}/${enterpriseId}`,
            );

            if (!response?.responseStatus?.isSuccess) {
                throw new Error(
                    response?.responseStatus?.error || "Failed to load subscribers.",
                );
            }

            const result = response?.result;
            const subscriberList = Array.isArray(result)
                ? result
                : result?.records || result?.subscribers || result?.items || [];

            const options = (Array.isArray(subscriberList) ? subscriberList : [])
                .filter((subscriber) => subscriber?.isEnterpriseUser === true)
                .map((subscriber) => {
                    const subscriberId = Number(
                        subscriber?.subscriberId ||
                        subscriber?.id ||
                        subscriber?.distributorUserId ||
                        subscriber?.userId ||
                        subscriber?.subscriberUserId ||
                        0,
                    );
                    const label = subscriber?.name || [subscriber?.firstName, subscriber?.lastName].filter(Boolean).join(" ") || subscriber?.email || "Subscriber";

                    return {
                        id: subscriberId,
                        value: subscriberId,
                        label,
                    };
                })
                .filter((option) => option.id > 0 && !selectedSubscribers.has(option.id));

            setModalSubscriberOptions(options);
            setIsAddSubscriberModalOpen(true);
        } catch (error) {
            toast.error(error?.message || "Failed to load subscribers.");
        } finally {
            showLoader(false);
        }
    };

    const closeAddSubscriberModal = () => {
        setIsAddSubscriberModalOpen(false);
        setModalSelectedSubscribers([]);
    };

    const saveAddSubscribers = async () => {
        const idsFromModal = modalSelectedSubscribers
            .map((subscriber) => Number(subscriber?.id || subscriber?.value || 0))
            .filter((id) => id > 0);

        if (!idsFromModal.length) return;

        try {
            showLoader(true);

            // API contract for add subscriber endpoint expects one payload at a time: { subscriberId }
            const responses = await Promise.all(
                idsFromModal.map((subscriberId) =>
                    ApiServices.httpPost(
                        ADDIN_CONFIG_ALL_SUBSUPDATE(enterpriseId, addInId),
                        { subscriberId },
                    ),
                ),
            );

            const failedResponse = responses.find(
                (response) => !response?.responseStatus?.isSuccess,
            );
            if (failedResponse) {
                throw new Error(
                    failedResponse?.responseStatus?.error || "Failed to associate subscribers.",
                );
            }

            const mergedSelected = new Set(selectedSubscribers);
            idsFromModal.forEach((id) => mergedSelected.add(id));
            setSelectedSubscribers(mergedSelected);

            if (onSubscribersChangeRef.current) {
                onSubscribersChangeRef.current(Array.from(mergedSelected));
            }

            await fetchSubscribers();
            toast.success("Subscribers associated successfully.");
            closeAddSubscriberModal();
        } catch (error) {
            toast.error(error?.message || "Failed to associate subscribers.");
        } finally {
            showLoader(false);
        }
    };

    const getSubscriberId = (subscriber) =>
        Number(subscriber?.subscriberId || subscriber?.id || subscriber?.distributorUserId || 0);

    const getFirstName = (subscriber) =>
        subscriber?.firstName || subscriber?.name?.split(" ")?.[0] || "-";

    const getLastName = (subscriber) =>
        subscriber?.lastName || subscriber?.name?.split(" ")?.slice(1).join(" ") || "-";

    const startCount = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
    const endCount = totalCount === 0 ? 0 : Math.min(pageNumber * pageSize, totalCount);

    return (
        <Box sx={{ mt: 2, mb: 2 }} data-testid="manage-subscriber">
            {/* Toolbar: Show entries + Search + Add Subscriber */}
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.95rem", color: "#555" }}>Show</Typography>
                    <Select
                        size="small"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        sx={{ minWidth: 70, height: 34, fontSize: "0.9rem" }}
                        disabled={loading}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                    <Typography sx={{ fontSize: "0.95rem", color: "#555" }}>entries</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <TextField
                        placeholder="Search subscribers..."
                        onChange={handleSearchChange}
                        variant="outlined"
                        size="small"
                        disabled={loading}
                        sx={{ width: 260 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#aaa", fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openAddSubscriberModal}
                        sx={{
                            textTransform: "none",
                            height: 40,
                            px: 2.5,
                            bgcolor: "#1976d2",
                            "&:hover": { bgcolor: "#1565c0" },
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                        }}
                    >
                        Add Subscriber
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ border: "1px solid #e0e0e0", boxShadow: "none", borderRadius: "6px" }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f6fa" }}>
                            {[
                                { label: "FIRST NAME", column: "firstName" },
                                { label: "LAST NAME", column: "lastName" },
                                { label: "EMAIL ADDRESS", column: "email" },
                            ].map((col) => {
                                return (
                                    <TableCell
                                        key={col.label}
                                        data-testid={`sort-header-${col.column}`}
                                        onClick={() => handleSortChange(col.column)}
                                        sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#555", letterSpacing: "0.04em", py: 1.5, cursor: "pointer", userSelect: "none" }}
                                    >
                                        {col.label}
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                verticalAlign: "middle",
                                                width: 14,
                                                justifyContent: "center",
                                            }}
                                        >
                                            <img
                                                src={getSortImage(col.column)}
                                                alt=""
                                                role="presentation"
                                                style={{ width: 10, height: 10 }}
                                            />
                                        </Box>
                                    </TableCell>
                                );
                            })}
                            <TableCell
                                sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#555", letterSpacing: "0.04em", textAlign: "left", py: 1.5 }}
                            >
                                ACCESS
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {subscribers.length > 0 ? (
                            subscribers.map((subscriber) => {
                                const subId = getSubscriberId(subscriber);
                                const isActive = selectedSubscribers.has(subId);
                                return (
                                    <TableRow
                                        key={subId || subscriber?.email || subscriber?.name}
                                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                                    >
                                        <TableCell sx={{ fontSize: "0.9rem", py: 1.5 }}>{getFirstName(subscriber)}</TableCell>
                                        <TableCell sx={{ fontSize: "0.9rem", py: 1.5 }}>{getLastName(subscriber)}</TableCell>
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Typography
                                                component="a"
                                                href={`mailto:${subscriber.email}`}
                                                sx={{ fontSize: "0.9rem", color: "#1976d2", textDecoration: "none" }}
                                            >
                                                {subscriber.email || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "left", py: 1.5 }}>
                                            <ToggleSwitch
                                                id={`subscriber-toggle-${subId}`}
                                                state={isActive}
                                                onChange={() => handleToggleSubscriber(subId)}
                                                disabled={loading}
                                                activeLabelText="Active"
                                                inactiveLabelText="Inactive"
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                                    <Typography color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                                        {loading ? "Loading subscribers..." : "No subscribers found"}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer: count + pagination */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1.5,
                    px: 0.5,
                }}
            >
                <Typography variant="body2" sx={{ color: "#888", fontSize: "0.85rem" }}>
                    Showing {startCount} to {endCount} of {totalCount} subscribers
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                        size="small"
                        aria-label="Previous page"
                        onClick={() => handlePageChange(null, pageNumber - 1)}
                        disabled={loading || pageNumber <= 1}
                        sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 0.5 }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            fontSize: "0.85rem",
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            color: "#444",
                            minWidth: 90,
                            textAlign: "center",
                        }}
                    >
                        Page {pageNumber} of {totalPages}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="Next page"
                        onClick={() => handlePageChange(null, pageNumber + 1)}
                        disabled={loading || pageNumber >= totalPages}
                        sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 0.5 }}
                    >
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            <Modal
                open={isAddSubscriberModalOpen}
                onClose={closeAddSubscriberModal}
                aria-labelledby="associate-subscriber-modal-title"
            >
                <Box sx={addSubscriberModalStyle}>
                    <div className="modal-header" style={{ borderBottom: "1px solid #e6e6e6", paddingBottom: "14px" }}>
                        <div className="modal-header-left" id="associate-subscriber-modal-title">
                            Associate Subscribers
                        </div>
                        <div style={{ marginTop: "-4px", marginLeft: "auto", paddingRight: "15px" }}>
                            <Button
                                className="action-button"
                                type="button"
                                variant="contained"
                                disabled={!modalSelectedSubscribers.length}
                                onClick={saveAddSubscribers}
                                style={{ minWidth: "220px", minHeight: "40px" }}
                            >
                                Save
                            </Button>
                        </div>
                        <button
                            type="button"
                            className="modal-header-right"
                            onClick={closeAddSubscriberModal}
                            style={{
                                cursor: "pointer",
                                background: "none",
                                border: "none",
                            }}
                            aria-label="Close"
                        >
                            <CloseOutlinedIcon />
                        </button>
                    </div>

                    <div className="modal-body manage-subscriber-body" style={{ paddingTop: "14px", minHeight: "300px" }}>
                        <Grid container spacing={2}>
                            <Grid
                                item
                                xs={12}
                                className="manage-subs-dropdown subscriber-serachable-dropdown"
                            >
                                <label htmlFor="searchSubscriber">Search Subscriber to associate</label>
                                <MandatoryField />
                                <MultiSelectCheckboxSearchableDropdown
                                    width="100%"
                                    multiple={true}
                                    from="subscriber"
                                    setData={(value) => {
                                        setModalSelectedSubscribers(value);
                                    }}
                                    name="searchSubscriber"
                                    value={modalSelectedSubscribers}
                                    id="searchSubscriber"
                                    className="multiple-associate-subscriber-dropdown"
                                    options={modalSubscriberOptions}
                                    placeholder="Search and Select Subscriber"
                                    showSelectAllOption={true}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </Box>
            </Modal>
        </Box>
    );
};

AddInsManageSubscriber.propTypes = {
    enterpriseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    addInId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSubscribersChange: PropTypes.func,
    assignmentMode: PropTypes.number,
    refreshKey: PropTypes.number,
};

AddInsManageSubscriber.defaultProps = {
    onSubscribersChange: null,
    assignmentMode: 2,
    refreshKey: 0,
};

export default AddInsManageSubscriber;
